import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  defaultParams,
  PriceSourceUnreachableError,
  ProblemError,
  StaleVersionError,
  type CalculationIn,
  type CalculationOut,
  type CalculationParamsOut,
  type CaseDetailOut,
} from "@/api";
import { Button } from "@/components/Button";
import { CancelledNotice } from "@/components/CancelledNotice";
import { DocumentsButton } from "@/components/DocumentsButton";
import { PageTitle } from "@/components/PageTitle";
import { Panel } from "@/components/Panel";
import { useScreenToast } from "@/components/Toast";
import { restorePath } from "@/lib/facadeEditability";
import { gratingTypeLabel } from "@/lib/gratingTypes";

import { ApprovalPanel } from "./ApprovalPanel";
import { CalculationBanner } from "./CalculationBanner";
import { ExportButton } from "./ExportButton";
import styles from "./CalculationScreen.module.css";
import { LineTable } from "./LineTable";
import { ParameterBar } from "./ParameterBar";
import { isReleased, quoteRelease } from "./readiness";
import { SupplierTable } from "./SupplierTable";
import { TotalsBlock } from "./TotalsBlock";
import { useRunCalculation } from "./useRunCalculation";

/** Statuses on which the calculation is frozen — every gesture on it answers 409 (design §8).
 * Exported: `EmptyCalculation` reads it too (fix round 2, item 15) for a
 * Vorgang that reached a frozen status without ever getting a calculation. */
export const FROZEN_STATUSES: ReadonlySet<CaseDetailOut["status"]> = new Set(["sent", "won", "lost"]);

/**
 * The mesh the next run takes because the Vorgang's type changed since the
 * row was priced (Rosttyp je Vorgang, R-9) — `null` where the stored mesh
 * stands.
 *
 * The stored mesh belongs to the type the row was priced for — a Pressrost's
 * 30/30 must not be re-sent for a Barfußrost, whose standard is 25/11 — so
 * it is the new type's standard (`defaultParams`, the server's own prefill).
 * A row that recorded no type (priced before the field existed) or a
 * Vorgang without one is not a difference: nothing is guessed.
 */
function meshAfterTypeChange(params: CalculationParamsOut, currentGratingType: string | null): string | null {
  const pricedFor = params.gratingType ?? null;
  if (pricedFor === null || currentGratingType === null || pricedFor === currentGratingType) return null;
  return defaultParams(currentGratingType).maschenweite ?? null;
}

/**
 * The stored parameters as the next request, against the Stand the Vorgang is
 * at now — with whatever one field changed.
 *
 * **One exception to „as stored“ (R-9):** the mesh after a type change
 * ({@link meshAfterTypeChange}). Material, bearing bar, anti-slip and margin
 * stay what the person chose, and a mesh the person picks in this very
 * gesture (`patch`) still wins.
 */
function inputFrom(
  params: CalculationParamsOut,
  expectedVersionNo: number,
  currentGratingType: string | null,
  patch: Partial<CalculationIn> = {},
): CalculationIn {
  return {
    expectedVersionNo,
    material: params.material,
    tragstab: params.tragstab,
    maschenweite: meshAfterTypeChange(params, currentGratingType) ?? params.maschenweite,
    rutschhemmung: params.rutschhemmung,
    marginPct: params.marginPct,
    // `?? null` (fix round 2, item 13): the generated field is optional, and
    // sending `undefined` on a JSON body drops the key instead of clearing
    // it — the same tolerance `ApprovalPanel` already applies elsewhere.
    supplierOverride: params.supplierOverride ?? null,
    ...patch,
  };
}

/** What the live region says about a failed run. Never silent: an error nobody named is an error nobody fixes. */
function runFailureLine(failure: Error | null): string | null {
  if (failure === null) return null;
  if (failure instanceof StaleVersionError) {
    return `Stand ${failure.latestVersionNo} ist inzwischen der letzte — neu rechnen.`;
  }
  if (failure instanceof PriceSourceUnreachableError) return failure.message;
  if (failure instanceof ProblemError) return failure.userMessage;
  return failure.message;
}

/**
 * `/vorgang/:caseId/kalkulation` with a calculation to show (design §9).
 *
 * Composition only — every figure is the server's, every rule is the
 * server's; this file decides what stands where and which controls are
 * open. Bar and approval are remounted per calculation (`key={calc.id}`) so
 * a typed margin, a ticked box or a received 409 never outlives the
 * calculation it belonged to. The live region is the screen's, so a
 * sentence about a landed gesture survives that remount.
 */
export function CalculationScreen({
  caseId,
  detail,
  calc,
}: {
  caseId: string;
  detail: CaseDetailOut;
  calc: CalculationOut;
}) {
  const navigate = useNavigate();
  const run = useRunCalculation(caseId);
  const toast = useScreenToast();
  /** The note of the last run, while it stands — one at a time, as on the board (review). */
  const runToastRef = useRef<string | null>(null);
  const [statusLine, setStatusLine] = useState("");
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Fix round 2, item 9: focus the screen's own heading the instant it
  // mounts — the landing spot for the empty state → screen swap (S-3, the
  // route's own live region carries the sentence; `CalculationRoute.tsx`)
  // and, just as usefully, for anyone routed here directly. Mount-only: a
  // rerun updates this same instance in place and must not steal focus back
  // from whatever a person is doing with the parameter bar.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const frozen = FROZEN_STATUSES.has(detail.status);
  // Storniert (spec 2026-10-15 §5, V-1): the server takes nothing but
  // „Wiederherstellen“ — no run, no approval, no supplier. Locked like a sent
  // Vorgang, but with its own notice and the way back to the record.
  const cancelled = detail.status === "cancelled";
  const closed = frozen || cancelled;
  const locked = run.isPending || isReleased(calc) || closed;
  // The quote file is the offer only while the release holds (E-52): released,
  // and still on the Stand the Vorgang stands at — the server's own test. The
  // button names why it is a draft, as the file does (Task 13d).
  const quote = quoteRelease(calc, detail.latestVersionNo);
  // Said, not only done (R-9): the bar shows the mesh the row was priced
  // with, the next run takes another — the person reads which before pressing.
  const nextMesh = closed ? null : meshAfterTypeChange(calc.params, detail.gratingType ?? null);
  const nextMeshLabel =
    nextMesh === null ? null : (calc.options.meshes.find((option) => option.value === nextMesh)?.label ?? nextMesh);

  function rerun(patch: Partial<CalculationIn> = {}): void {
    // Read before the run: the answer moves the Vorgang on, and the sentence
    // is about where it stood when the person pressed.
    const backInPlanning = detail.status === "planning_in_progress";
    run.mutate(inputFrom(calc.params, detail.latestVersionNo, detail.gratingType, patch), {
      onSuccess: ({ calc: next, created }) => {
        const sentence = created
          ? `Kalkulation ${next.calcNo} gerechnet.`
          : backInPlanning
            ? // The same calculation, taken up again (B-1) — „unverändert“
              // would read as if the press had done nothing.
              `Neu gerechnet — Kalkulation ${next.calcNo} gilt unverändert.`
            : "Unverändert — dieselben Parameter auf demselben Preisstand.";
        setStatusLine(sentence);
        // Seen where the press was (Welle 1, idea 23) — the live line above
        // says it, so the toast is for the eye only. Every parameter is a
        // run: the newest note says what the older ones did, so it sends them away.
        if (runToastRef.current !== null) toast.dismiss(runToastRef.current);
        runToastRef.current = toast.show({ text: sentence, announce: false });
      },
    });
  }

  // Fix round 2, item 8: the 503 used to blank this line so its own banner
  // (`CalculationBanner`, a fresh `role="status"` mounted already carrying
  // its text) would not say the same thing twice — the wrong trade, because
  // a freshly-mounted region is not reliably announced at all, unlike a
  // *change* inside this persistent one. `runFailureLine` already turns a
  // `PriceSourceUnreachableError` into the same sentence `unreachable` reads
  // below, so no special case is needed here any more.
  const unreachable = run.error instanceof PriceSourceUnreachableError ? run.error.message : null;
  const live = run.isPending ? "Rechnet neu …" : (runFailureLine(run.error) ?? statusLine);

  return (
    <div className={styles.screen}>
      <header className={styles.head}>
        <PageTitle headingRef={headingRef} tabIndex={-1} className={styles.title}>
          Kalkulation
        </PageTitle>
        <p className={styles.lede}>
          Jede Position mit Betrag, Basis und Preisquelle. Freigegeben wird von Hand.
        </p>
        {/* The Vorgang is named by the frame above this page (design
            2026-10-22 §5): the meta line begins with the Stand. */}
        <p className={styles.meta}>
          Stand <span className="num">{calc.planVersionNo}</span> · Kalkulation{" "}
          <span className="num">{calc.calcNo}</span> · Lieferant {calc.supplier ?? "—"}
        </p>
      </header>

      {cancelled ? (
        <CancelledNotice
          cancellation={detail.cancellation ?? null}
          action={
            <Button
              variant="primary"
              aria-describedby="case-cancelled"
              onClick={() => navigate(restorePath(caseId))}
            >
              Wiederherstellen …
            </Button>
          }
        />
      ) : (
        <CalculationBanner
          calc={calc}
          detail={detail}
          frozen={frozen}
          unreachable={unreachable}
          busy={run.isPending}
          onRerun={() => rerun()}
        />
      )}

      <ParameterBar
        key={calc.id}
        params={calc.params}
        options={calc.options}
        disabled={locked}
        runFailed={run.isError}
        onChange={(patch) => rerun(patch)}
      />

      {nextMeshLabel !== null ? (
        <p className={styles.meshNote}>
          Der Rosttyp ist jetzt {gratingTypeLabel(detail.gratingType!)}: „Neu rechnen“ nimmt Masche{" "}
          <span className="num">{nextMeshLabel}</span>.
        </p>
      ) : null}

      <p role="status" className={styles.live}>
        {live}
      </p>

      <Panel
        title="Positionen"
        hint="Roste nach Einkauf und Marge, Konsolen zum JTL-Verkaufspreis, Ausschnitte und Fracht dazu."
      >
        <LineTable lines={calc.lines} />
      </Panel>

      <Panel title="Summen" hint="Zwei Margen, weil Konsolen keinen Einkaufspreis tragen.">
        <TotalsBlock calc={calc} />
      </Panel>

      <Panel
        title="Lieferanten"
        hint="Alle aktiven Lieferanten durchgerechnet — der günstigste vollständige ist der Vorschlag."
      >
        <SupplierTable
          calc={calc}
          disabled={locked}
          onChoose={(supplierOverride) => rerun({ supplierOverride })}
        />
      </Panel>

      <Panel title="Freigabe" hint="Erst die Freigabe macht aus der Kalkulation ein Angebot.">
        <ApprovalPanel
          key={calc.id}
          caseId={caseId}
          calc={calc}
          detail={detail}
          frozen={closed}
          busy={run.isPending}
          onDone={setStatusLine}
          onLeaveReleased={() => headingRef.current?.focus()}
        />
      </Panel>

      <div className={styles.footer}>
        <Button variant="quiet" onClick={() => navigate(`/vorgang/${caseId}/planung`)}>
          Zurück zur Planung
        </Button>
        <ExportButton caseId={caseId} calcId={calc.id} variant="angebot" release={quote} onDone={setStatusLine} />
        <ExportButton caseId={caseId} calcId={calc.id} variant="intern" onDone={setStatusLine} />
        <DocumentsButton caseId={caseId} versionNo={detail.latestVersionNo} />
      </div>
    </div>
  );
}
